import { useState } from 'react';
import { api } from '../lib/api.js';

// ─── Suggest Task Modal ───────────────────────────────────────────────────────
export function SuggestTaskModal({ user, locationId, timePeriods, onClose, onSubmit }) {
  const [form, setForm] = useState({ title: '', description: '', suggested_period: timePeriods[0]?.name || '', sop_steps: [] });
  const [showSOP, setShowSOP] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const steps = showSOP ? form.sop_steps.filter(s => s.trim()) : [];
      await api('POST', '/api/task-suggestions', {
        submitted_by: user.id,
        location_id: locationId,
        title: form.title.trim(),
        description: form.description.trim() || null,
        suggested_period: form.suggested_period || null,
        sop_steps_json: steps.length > 0 ? JSON.stringify(steps) : null,
      });
      setDone(true);
      onSubmit();
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-cream w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col fade-in shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="bg-gold rounded-t-2xl p-5 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-ink/60 text-xs uppercase tracking-widest mb-0.5">Staff Suggestion</p>
            <h3 className="text-ink font-bold text-lg">Suggest a Task / SOP</h3>
          </div>
          <button onClick={onClose} className="text-ink/50 hover:text-ink text-3xl leading-none">×</button>
        </div>
        {done ? (
          <div className="p-10 text-center">
            <div className="text-5xl mb-4">✅</div>
            <p className="font-bold text-lg text-ink mb-1">Suggestion submitted!</p>
            <p className="text-sm text-gray-500 mb-6">A manager will review your suggestion soon.</p>
            <button onClick={onClose} className="w-full py-3 bg-crimson text-white font-semibold rounded-xl hover:bg-crimson-dark">Done</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-5 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Task Name *</label>
              <input value={form.title} onChange={e => set('title', e.target.value)} required autoFocus
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-gold/40"
                placeholder="e.g. Check outdoor seating area" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Description</label>
              <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-ink bg-white focus:outline-none resize-none"
                placeholder="What needs to be done and why..." />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Suggested Time Period</label>
              <select value={form.suggested_period} onChange={e => set('suggested_period', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-ink bg-white focus:outline-none">
                <option value="">— No preference —</option>
                {timePeriods.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">SOP Steps (optional)</label>
                <button type="button" onClick={() => setShowSOP(s => !s)}
                  className={`text-xs px-2 py-1 rounded border transition-all ${showSOP ? 'bg-gold text-ink border-gold' : 'bg-white text-gray-500 border-gray-200'}`}>
                  {showSOP ? 'Remove' : '+ Add SOP'}
                </button>
              </div>
              {showSOP && (
                <div className="space-y-2 fade-in">
                  {form.sop_steps.map((step, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <span className="text-xs text-gray-400 w-5 text-right flex-shrink-0">{i+1}.</span>
                      <input value={step} onChange={e => { const s=[...form.sop_steps]; s[i]=e.target.value; set('sop_steps',s); }}
                        className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none"
                        placeholder={`Step ${i+1}`} />
                      <button type="button" onClick={() => set('sop_steps', form.sop_steps.filter((_,j)=>j!==i))}
                        className="text-red-400 hover:text-red-600 text-lg px-1">×</button>
                    </div>
                  ))}
                  <button type="button" onClick={() => set('sop_steps', [...form.sop_steps, ''])}
                    className="text-sm text-gold hover:text-yellow-600 font-medium">+ Add Step</button>
                </div>
              )}
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 text-sm">Cancel</button>
              <button type="submit" disabled={saving || !form.title.trim()}
                className="flex-1 py-3 rounded-xl bg-gold text-ink font-semibold hover:bg-gold-light disabled:opacity-50 active:scale-98 transition-all text-sm">
                {saving ? 'Submitting...' : 'Submit Suggestion'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
